import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle,
  FileText,
  ArrowRight,
  Clock,
  Globe,
  Scale
} from 'lucide-react';
import { getText, getCurrentLanguage } from '../services/dataSync';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [quickLinks, setQuickLinks] = useState([]);
  const [usefulLinks, setUsefulLinks] = useState([]);
  const [supportLinks, setSupportLinks] = useState([]);
  const [legalLinks, setLegalLinks] = useState([]);

  // Update links when language changes
  const updateLinks = () => {
    setQuickLinks([
      { name: getText('home.contact.aboutUs') || 'About Us', to: '/about' },
      { name: getText('footer.missionVision') || 'Mission & Vision', to: '/about/mission-vision' },
      { name: getText('nav.about.submenu.leadershipTeam') || 'Leadership Team', to: '/about/leadership-team' },
      { name: getText('home.contact.committee') || 'Committee', to: '/about/committee' },
      { name: getText('home.contact.presidentMessage') || 'President Message', to: '/about/president' }
    ]);

    setUsefulLinks([
      { name: getText('footer.services') || 'Services', to: '/services' },
      { name: getText('footer.events') || 'Events', to: '/events' },
      { name: getText('footer.news') || 'News', to: '/news' },
      { name: getText('footer.membership') || 'Membership', to: '/membership' },
    ]);

    setSupportLinks([
      { name: getText('footer.contactUs') || 'Contact Us', to: '/contact' },
      { name: getText('footer.grievance') || 'Grievance', to: '/grievance' },
      { name: getText('footer.memberRegistration') || 'Member Registration', to: '/member-registration' },
      { name: getText('footer.renewMembership') || 'Renew Membership', to: '/renewal' }
    ]);

    setLegalLinks([
      { name: getText('footer.privacyPolicy') || 'Privacy Policy', to: '/privacy-policy' },
      { name: getText('footer.termsConditions') || 'Terms & Conditions', to: '/terms-conditions' },
      { name: getText('footer.refundPolicy') || 'Refund Policy', to: '/refund-policy' }
    ]);
  };

  // Listen for language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      setCurrentLang(getCurrentLanguage());
      updateLinks();
    };

    // Initial load
    updateLinks();

    // Listen for custom language change events
    window.addEventListener('languageChanged', handleLanguageChange);
    
    // Listen for storage changes (for cross-tab synchronization)
    window.addEventListener('storage', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);


  return (
    <footer className="footer-modern">
      {/* Wave Top Border */}
      <div className="footer-wave-top">
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="footer-wave-svg">
          <path d="M0,60 L0,40 C180,20 360,50 540,35 C720,20 900,50 1080,35 C1260,20 1440,50 1440,35 L1440,60 Z" fill="#2563eb" />
        </svg>
      </div>
      
      <div className="footer-container">
        {/* Main Grid Layout */}
        <div className="footer-grid">
          {/* Column 1: Contact Information */}
          <div className="footer-column">
            <div className="footer-column-header">
              <MapPin className="footer-column-icon" />
              <h4 className="footer-column-title">
                {getText('home.contact.address') || 'Address'}
              </h4>
            </div>
            <div className="footer-contact-info">
              <div className="footer-address-wrapper">
                <p className="footer-address">
                  Office no.9, A-wing, Jewel of Pimpri,<br />
                  Kamla Cross Road, Pimpri,<br />
                  Pune-411018
                </p>
              </div>
              <div className="footer-contact-items">
                <a 
                  href="tel:9067205252" 
                  className="footer-contact-item"
                >
                  <Phone className="footer-contact-icon" />
                  <span>9067205252</span>
                </a>
                <a 
                  href="mailto:support@mahaifm.com" 
                  className="footer-contact-item"
                >
                  <Mail className="footer-contact-icon" />
                  <span>support@mahaifm.com</span>
                </a>
                <a 
                  href="mailto:Info@mahaifm.com" 
                  className="footer-contact-item"
                >
                  <Mail className="footer-contact-icon" />
                  <span>Info@mahaifm.com</span>
                </a>
                <div className="footer-contact-item">
                  <Clock className="footer-contact-icon" />
                  <span>{getText('footer.workingHours') || 'Mon - Fri: 9:00 AM - 6:00 PM'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-column">
            <div className="footer-column-header">
              <FileText className="footer-column-icon" />
              <h4 className="footer-column-title">
                {getText('footer.quickLinks') || 'Quick Links'}
              </h4>
            </div>
            <ul className="footer-links">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    <ArrowRight className="footer-link-icon" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Resources */}
          <div className="footer-column">
            <div className="footer-column-header">
              <Globe className="footer-column-icon" />
              <h4 className="footer-column-title">
                {getText('footer.resources') || 'Resources'}
              </h4>
            </div>
            <ul className="footer-links">
              {usefulLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    <ArrowRight className="footer-link-icon" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Support */}
          <div className="footer-column">
            <div className="footer-column-header">
              <MessageCircle className="footer-column-icon" />
              <h4 className="footer-column-title">
                {getText('footer.support') || 'Support'}
              </h4>
            </div>
            <ul className="footer-links">
              {supportLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    <ArrowRight className="footer-link-icon" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Legal & Policies */}
          <div className="footer-column">
            <div className="footer-column-header">
              <Scale className="footer-column-icon" />
              <h4 className="footer-column-title">
                {getText('footer.legalPolicies') || 'Legal & Policies'}
              </h4>
            </div>
            <ul className="footer-links">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.to} className="footer-link">
                    <ArrowRight className="footer-link-icon" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <div className="footer-copyright">
              <p className="footer-copyright-text">
                © {currentYear} {getText('footer.organizationName') || 'Industrial Fedration of Maharashtra Region'}. {getText('home.contact.copyright') || 'All rights reserved'}.
              </p>
            </div>
            <div className="footer-credits">
              <p className="footer-credits-text">
                {getText('footer.designedBy') || 'Designed and Developed by'}{' '}
                <span className="footer-credits-company">Infoprosys Technologies Pvt Ltd</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
